import React, { useState } from "react";
import { Camera, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const MAX_REVIEW_PHOTOS = 3;

export function ReviewPhotoPicker({ photos, onChange }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_REVIEW_PHOTOS - photos.length);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/reviews/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      onChange([...photos, ...urls]);
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} added 📸`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="mb-3" data-testid="review-photo-picker">
      <div className="flex items-center gap-2 flex-wrap">
        {photos.map((u, i) => (
          <div key={u} className="relative">
            <img src={u} alt="" className="w-16 h-16 rounded-xl object-cover ring-2 ring-[#FFD93D]" data-testid={`review-photo-preview-${i}`} />
            <button type="button" onClick={() => onChange(photos.filter((p) => p !== u))} aria-label="Remove photo" data-testid={`review-photo-remove-${i}`}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#FF6B9D] text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
          </div>
        ))}
        {photos.length < MAX_REVIEW_PHOTOS && (
          <label className={`w-16 h-16 rounded-xl border-2 border-dashed border-[#19324A]/20 hover:border-[#FF6B9D] flex flex-col items-center justify-center text-[#19324A]/50 cursor-pointer text-[10px] font-semibold ${uploading ? "opacity-50" : ""}`}>
            <Camera className="w-5 h-5 mb-0.5" />{uploading ? "…" : "Add pup pic"}
            <input type="file" accept="image/*" multiple onChange={upload} className="hidden" disabled={uploading} data-testid="review-photo-input" />
          </label>
        )}
      </div>
      <p className="text-[11px] text-[#19324A]/45 mt-1">Up to {MAX_REVIEW_PHOTOS} photos — show us your Bulldog with it! 🐶</p>
    </div>
  );
}

export function ReviewPhotos({ photos = [], testid = "review-photos" }) {
  const [open, setOpen] = useState(null);
  if (!photos.length) return null;
  return (
    <>
      <div className="flex gap-2 mt-3 flex-wrap" data-testid={testid}>
        {photos.map((u, i) => (
          <button key={u} type="button" onClick={() => setOpen(u)} aria-label="View photo" data-testid={`${testid}-${i}`} className="rounded-xl overflow-hidden ring-2 ring-white soft-shadow hover:scale-105 transition-transform">
            <img src={u} alt="Customer photo" loading="lazy" className="w-20 h-20 object-cover" />
          </button>
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 z-[80] bg-[#19324A]/85 flex items-center justify-center p-4" onClick={() => setOpen(null)} data-testid="review-photo-lightbox">
          <img src={open} alt="Customer photo" className="max-w-full max-h-[85vh] rounded-3xl soft-shadow" />
          <button type="button" aria-label="Close" onClick={() => setOpen(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
      )}
    </>
  );
}
